// screens/FlightDetailsScreen.js
import React, { useState, useEffect, useContext } from 'react';
import { 
  useParams, 
  useLocation, 
  useNavigate,
  Link 
} from 'react-router-dom';
import { 
  ArrowLeft, 
  Calendar, 
  Clock, 
  Users, 
  Shield, 
  Wifi, 
  Coffee,
  Luggage,
  MapPin,
  Star,
  CheckCircle,
  ChevronRight,
  CreditCard,
  Lock,
  Plane,
  AlertCircle,
  Heart,
  Share2,
  Download,
  ExternalLink
} from 'lucide-react';
import { AuthContext } from '../../../../Context/AuthContext';
import FlightService from './FlightService';

// ======================================================
// EXPEDIA REDIRECT UTILITY FUNCTIONS
// ======================================================

// Extract 3-letter airport code from various formats
const extractAirportCode = (input) => {
  if (!input) return '';
  
  // Handle object with code property
  if (typeof input === 'object' && input.code) {
    return input.code.toUpperCase();
  }
  
  // If it's already a 3-letter code, return it uppercase
  if (typeof input === 'string' && input.length === 3 && /^[A-Z]{3}$/.test(input.toUpperCase())) {
    return input.toUpperCase();
  }
  
  // Try to extract code from string like "DUBAI (DXB) - INTERNATIONAL"
  const match = String(input).match(/\(([A-Z]{3})\)/);
  if (match && match[1]) {
    return match[1];
  }
  
  // Handle string like "JFK - John F. Kennedy International"
  const codeMatch = String(input).match(/^([A-Z]{3})/);
  if (codeMatch && codeMatch[1]) {
    return codeMatch[1];
  }
  
  // Return original string uppercased if no pattern matched
  return String(input).toUpperCase().substring(0, 3);
};

// Format date for URL (YYYY-MM-DD)
const formatDateForURL = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  return date.toISOString().split('T')[0];
};

// Add currency parameter to existing URL
const addCurrencyToUrl = (url, currency) => {
  try {
    const urlObj = new URL(url);
    const params = new URLSearchParams(urlObj.search);
    
    // Add multiple currency parameter variations
    if (!params.has('currency')) params.append('currency', currency);
    if (!params.has('selectedCurrency')) params.append('selectedCurrency', currency);
    if (!params.has('preferredCurrency')) params.append('preferredCurrency', currency);
    if (!params.has('curr')) params.append('curr', currency);
    if (!params.has('currencyCode')) params.append('currencyCode', currency);
    
    // Add market and locale parameters
    if (!params.has('market')) params.append('market', 'global');
    if (!params.has('locale')) params.append('locale', getLocaleFromCurrency(currency));
    
    urlObj.search = params.toString();
    return urlObj.toString();
  } catch (error) {
    console.error('Error adding currency to URL:', error);
    return url;
  }
};

// Get locale from currency code
const getLocaleFromCurrency = (currency) => {
  const localeMap = {
    'USD': 'en-US',
    'EUR': 'en-GB',
    'GBP': 'en-GB',
    'JPY': 'ja-JP',
    'CNY': 'zh-CN',
    'INR': 'en-IN',
    'AUD': 'en-AU',
    'CAD': 'en-CA',
    'CHF': 'de-CH',
    'SGD': 'en-SG',
    'AED': 'en-AE',
    'SAR': 'ar-SA',
    'KRW': 'ko-KR',
    'MXN': 'es-MX',
    'BRL': 'pt-BR',
    'ZAR': 'en-ZA',
    'THB': 'th-TH',
    'VND': 'vi-VN',
    'IDR': 'id-ID',
    'MYR': 'ms-MY',
    'PHP': 'en-PH',
    'PKR': 'ur-PK',
    'BDT': 'bn-BD',
    'LKR': 'si-LK',
    'NPR': 'ne-NP',
    'MMK': 'my-MM',
    'UAH': 'uk-UA',
    'PLN': 'pl-PL',
    'CZK': 'cs-CZ',
    'SEK': 'sv-SE',
    'NOK': 'no-NO',
    'DKK': 'da-DK',
    'RUB': 'ru-RU',
    'TRY': 'tr-TR',
    'HKD': 'zh-HK',
    'NZD': 'en-NZ',
    'ILS': 'he-IL',
    'EGP': 'ar-EG',
    'IQD': 'ar-IQ',
    'KWD': 'ar-KW',
    'OMR': 'ar-OM',
    'QAR': 'ar-QA',
    'BHD': 'ar-BH'
  };
  
  return localeMap[currency] || 'en-US';
};

// Enhanced URL construction with better Expedia currency support
const constructExpediaDeepLink = (flight, currency = 'USD') => {
  try {
    const departureCode = extractAirportCode(flight.departure?.code || flight.departureAirport);
    const arrivalCode = extractAirportCode(flight.destination?.code || flight.arrivalAirport);
    const departureDate = formatDateForURL(flight.departure?.time || flight.departureTime);
    const returnDate = flight.destination?.time ? formatDateForURL(flight.destination?.time) : 
                      (flight.returnDate ? formatDateForURL(flight.returnDate) : null);
    const tripType = flight.tripType || 'roundTrip';
    
    if (!departureCode || !arrivalCode || !departureDate) {
      // console.log('Missing required flight data');
      return null;
    }

    // Build Expedia URL with comprehensive currency parameters
    const baseParams = {
      trip: tripType === 'oneWay' ? 'oneway' : 'roundtrip',
      leg1: `${departureCode},${arrivalCode},${departureDate}`,
      passengers: `adults:${flight.travelerCount?.adults || 1},children:${flight.travelerCount?.children || 0},infantinlap:${flight.travelerCount?.infants || 0}`,
      options: 'cabinclass:economy',
      mode: 'search'
    };

    // Add return leg for round trips
    if (tripType !== 'oneWay' && returnDate) {
      baseParams.leg2 = `${arrivalCode},${departureCode},${returnDate}`;
    }

    // Create URL with comprehensive currency parameter variations
    const params = new URLSearchParams(baseParams);
    
    // Add multiple currency parameters that Expedia recognizes
    params.append('currency', currency);
    params.append('selectedCurrency', currency);
    params.append('preferredCurrency', currency);
    params.append('curr', currency); 
    params.append('currencyCode', currency); 
    
    // Add market and locale parameters for better currency support
    params.append('market', 'global');
    params.append('locale', getLocaleFromCurrency(currency));
    
    const expediaUrl = `https://www.expedia.com/Flights-Search?${params.toString()}`;
    
    return expediaUrl;

  } catch (error) {
    console.error('Error constructing Expedia URL:', error);
    return null;
  }
};

// Main handler for booking on Expedia
const handleBookOnExpedia = (flight, currency = 'USD') => {
  // If flight has a direct link and it's not the homepage, use it
  if (flight.link && !flight.link.includes('expedia.com/home')) {
    // Enhance the existing link with project currency parameter
    let enhancedUrl = addCurrencyToUrl(flight.link, currency);
    
    // Open in new tab
    window.open(enhancedUrl, '_blank', 'noopener,noreferrer');
    return;
  } else {
    // Construct a new Expedia URL with project currency
    const expediaUrl = constructExpediaDeepLink(flight, currency);
    
    if (expediaUrl) {
      // Open in new tab
      window.open(expediaUrl, '_blank', 'noopener,noreferrer');
    } else {
      alert('Booking link not available for this flight');
    }
  }
};

// ======================================================
// BOOKING.COM HOTEL PACKAGE UTILITY FUNCTIONS
// ======================================================

// Extract city name from airport code or location string
const extractCityName = (input) => {
  if (!input) return '';
  
  // Handle object with name property
  if (typeof input === 'object' && input.name) {
    const name = input.name;
    // Remove airport code patterns like "(OSL)" or "- Gardermoen"
    return name.split('(')[0].split('-')[0].trim();
  }
  
  // Handle string like "DUBAI (DXB) - INTERNATIONAL" or "Oslo (OSL) - Gardermoen"
  if (typeof input === 'string') {
    // Remove airport code patterns
    const cleaned = input.split('(')[0].split('-')[0].trim();
    return cleaned;
  }
  
  // If it's already a 3-letter code, try to use it directly
  if (typeof input === 'string' && input.length === 3) {
    return input;
  }
  
  return String(input);
};

// Format date for Booking.com URL (DD/MM/YYYY)
const formatDateForBooking = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

// Construct Booking.com hotel search URL
const constructBookingHotelUrl = (destination, checkInDate, checkOutDate, guests = { adults: 1, children: 0 }, rooms = 1) => {
  try {
    const cityName = extractCityName(destination);
    
    if (!cityName) {
      // console.log('Missing destination city');
      return null;
    }

    const checkIn = formatDateForBooking(checkInDate);
    const checkOut = formatDateForBooking(checkOutDate);

    // Get guest counts
    const adults = guests?.adults || 1;
    const children = guests?.children || 0;

    // Build Booking.com URL with proper parameters
    let url = `https://www.booking.com/searchresults.html?ss=${encodeURIComponent(cityName)}`;
    
    if (checkIn) {
      url += `&checkin_date=${checkIn}`;
    }
    if (checkOut) {
      url += `&checkout_date=${checkOut}`;
    }
    
    url += `&group_adults=${adults}`;
    url += `&group_children=${children}`;
    url += `&no_rooms=${rooms}`;
    
    // console.log('Booking.com Hotel URL:', url);
    return url;
    
  } catch (error) {
    console.error('Error constructing Booking.com hotel URL:', error);
    return null;
  }
};

// Handler for booking hotel package on Booking.com
const handleHotelPackageOnBooking = (flight, searchParams = {}) => {
  try {
    // Extract destination city from flight
    const destination = flight.destination || flight.arrivalAirport;
    
    // Get check-in date (departure date)
    const checkInDate = flight.departure?.time || flight.departureTime || searchParams.departureDate;
    
    // Get check-out date (return date) - if not available, default to 3 days after check-in
    let checkOutDate = flight.destination?.time || flight.arrivalTime || searchParams.returnDate;
    if (!checkOutDate && checkInDate) {
      const returnDate = new Date(checkInDate);
      returnDate.setDate(returnDate.getDate() + 3); // Default 3-night stay
      checkOutDate = returnDate.toISOString();
    }
    
    // Get guest information
    const guests = {
      adults: searchParams?.adults || flight.travelerCount?.adults || 1,
      children: searchParams?.children || flight.travelerCount?.children || 0
    };
    
    // Construct Booking.com URL
    const bookingUrl = constructBookingHotelUrl(destination, checkInDate, checkOutDate, guests);
    
    if (bookingUrl) {
      // Open in new tab
      window.open(bookingUrl, '_blank', 'noopener,noreferrer');
    } else {
      alert('Hotel booking link not available for this flight');
    }
    
  } catch (error) {
    console.error('Error handling hotel package:', error);
    alert('Failed to open hotel booking. Please try again.');
  }
};

const FlightDetails = () => {
  const { id, frondId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  const flightService = FlightService;
  
  // Get flight data from location state or fetch from API
  const flightFromState = location.state?.flight;
  const searchParams = location.state?.searchParams || {};
  
  // State
  const [flight, setFlight] = useState(flightFromState);
  const [loading, setLoading] = useState(!flightFromState);
  const [error, setError] = useState(null);
  const [selectedFare, setSelectedFare] = useState('economy');
  const [passengerDetails, setPassengerDetails] = useState({
    adults: searchParams?.adults || 1,
    children: searchParams?.children || 0,
    infants: searchParams?.infants || 0
  });
  const [showBookingForm, setShowBookingForm] = useState(false);

  // Fetch flight details if not passed in state
  useEffect(() => {
    if (!flightFromState && id) {
      fetchFlightDetails();
    }
  }, [id, flightFromState]);

  const fetchFlightDetails = async () => {
    try {
      setLoading(true);
      const details = await flightService.getFlightDetails(id);
      setFlight(details);
    } catch (err) {
      console.error('Error fetching flight details:', err);
      setError('Failed to load flight details');
    } finally {
      setLoading(false);
    }
  };

  // Helper functions
  const formatTime = (timeString) => {
    if (!timeString) return '--:--';
    const date = new Date(timeString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '--h --m';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const formatPrice = (price, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(price || 0);
  };

  // Generate itinerary content - HTML version for better styling
  const generateItineraryHTML = () => {
    const fareType = fareTypes.find(f => f.id === selectedFare);
    const totalTravelers = passengerDetails.adults + passengerDetails.children + passengerDetails.infants;
    const baseFare = (fareType?.price || flight.price) * totalTravelers;
    const total = baseFare + 60;
    const bookingRef = Math.random().toString(36).substring(2, 10).toUpperCase();
    
    const htmlContent = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Flight Itinerary - ${bookingRef}</title>
    <style>
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            min-height: 100vh;
            padding: 40px 20px;
        }
        .itinerary-container {
            max-width: 800px;
            margin: 0 auto;
            background: #ffffff;
            border-radius: 20px;
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
            overflow: hidden;
        }
        .header {
            background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
            color: white;
            padding: 30px 40px;
            text-align: center;
        }
        .header h1 {
            font-size: 28px;
            font-weight: 700;
            margin-bottom: 10px;
        }
        .header .booking-ref {
            font-size: 14px;
            opacity: 0.9;
        }
        .header .booking-ref span {
            background: rgba(255,255,255,0.2);
            padding: 5px 15px;
            border-radius: 20px;
            font-weight: 600;
            letter-spacing: 2px;
        }
        .section {
            padding: 30px 40px;
            border-bottom: 1px solid #eee;
        }
        .section-title {
            font-size: 18px;
            font-weight: 600;
            color: #1e3c72;
            margin-bottom: 20px;
            display: flex;
            align-items: center;
            gap: 10px;
        }
        .section-title::before {
            content: '';
            width: 4px;
            height: 20px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border-radius: 2px;
        }
        .passenger-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 15px;
        }
        .passenger-item {
            background: #f8f9fc;
            padding: 15px;
            border-radius: 10px;
            text-align: center;
        }
        .passenger-item .label {
            font-size: 12px;
            color: #888;
            margin-bottom: 5px;
        }
        .passenger-item .value {
            font-size: 24px;
            font-weight: 700;
            color: #1e3c72;
        }
        .flight-route {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 20px 0;
        }
        .airport {
            text-align: center;
            flex: 1;
        }
        .airport .code {
            font-size: 36px;
            font-weight: 700;
            color: #1e3c72;
        }
        .airport .name {
            font-size: 12px;
            color: #888;
            margin-top: 5px;
        }
        .airport .time {
            font-size: 18px;
            font-weight: 600;
            color: #333;
            margin-top: 5px;
        }
        .airport .date {
            font-size: 12px;
            color: #888;
        }
        .flight-arrow {
            flex: 1;
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: 0 20px;
        }
        .flight-arrow .duration {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 8px 20px;
            border-radius: 20px;
            font-size: 14px;
            font-weight: 600;
            margin-bottom: 10px;
        }
        .flight-arrow .line {
            width: 100%;
            height: 2px;
            background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
            position: relative;
        }
        .flight-arrow .line::before,
        .flight-arrow .line::after {
            content: '';
            position: absolute;
            width: 10px;
            height: 10px;
            background: #1e3c72;
            border-radius: 50%;
            top: 50%;
            transform: translateY(-50%);
        }
        .flight-arrow .line::before { left: -5px; }
        .flight-arrow .line::after { right: -5px; }
        .flight-arrow .stops {
            font-size: 12px;
            color: #888;
            margin-top: 10px;
        }
        .flight-info {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 15px;
        }
        .flight-info-item {
            background: #f8f9fc;
            padding: 15px;
            border-radius: 10px;
        }
        .flight-info-item .label {
            font-size: 12px;
            color: #888;
            margin-bottom: 5px;
        }
        .flight-info-item .value {
            font-size: 16px;
            font-weight: 600;
            color: #333;
        }
        .price-table {
            width: 100%;
        }
        .price-row {
            display: flex;
            justify-content: space-between;
            padding: 12px 0;
            border-bottom: 1px solid #eee;
        }
        .price-row:last-child {
            border-bottom: none;
        }
        .price-row.total {
            background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
            color: white;
            margin: 15px -40px -30px;
            padding: 20px 40px;
            font-size: 20px;
            font-weight: 700;
        }
        .amenities-list {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 10px;
        }
        .amenity-item {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 10px 15px;
            background: #f8f9fc;
            border-radius: 8px;
        }
        .amenity-item .icon {
            width: 24px;
            height: 24px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-size: 12px;
        }
        .info-box {
            background: #fff9e6;
            border-left: 4px solid #ffc107;
            padding: 15px 20px;
            border-radius: 0 10px 10px 0;
            margin-bottom: 15px;
        }
        .info-box h4 {
            color: #856404;
            margin-bottom: 8px;
            font-size: 14px;
        }
        .info-box p {
            color: #856404;
            font-size: 13px;
            line-height: 1.6;
        }
        .footer {
            background: #f8f9fc;
            padding: 20px 40px;
            text-align: center;
            font-size: 12px;
            color: #888;
        }
        .footer .logo {
            font-size: 20px;
            font-weight: 700;
            color: #1e3c72;
            margin-bottom: 5px;
        }
        @media print {
            body {
                background: white;
                padding: 0;
            }
            .itinerary-container {
                box-shadow: none;
                max-width: 100%;
            }
        }
    </style>
</head>
<body>
    <div class="itinerary-container">
        <div class="header">
            <h1>✈️ Flight Itinerary</h1>
            <p class="booking-ref">Booking Reference: <span>${bookingRef}</span></p>
        </div>
        
        <div class="section">
            <h3 class="section-title">Passenger Details</h3>
            <div class="passenger-grid">
                <div class="passenger-item">
                    <div class="label">Adults (12+)</div>
                    <div class="value">${passengerDetails.adults}</div>
                </div>
                <div class="passenger-item">
                    <div class="label">Children (2-11)</div>
                    <div class="value">${passengerDetails.children}</div>
                </div>
                <div class="passenger-item">
                    <div class="label">Infants (<2)</div>
                    <div class="value">${passengerDetails.infants}</div>
                </div>
            </div>
        </div>
        
        <div class="section">
            <h3 class="section-title">Flight Details</h3>
            <div class="flight-route">
                <div class="airport">
                    <div class="code">${flight.departure?.code || 'N/A'}</div>
                    <div class="name">${flight.departure?.name || 'Airport'}</div>
                    <div class="time">${formatTime(flight.departure?.time)}</div>
                    <div class="date">${formatDate(flight.departure?.time)}</div>
                </div>
                <div class="flight-arrow">
                    <div class="duration">${formatDuration(flight.durationMinutes)}</div>
                    <div class="line"></div>
                    <div class="stops">${flight.stops === 0 ? 'Non-stop' : `${flight.stops} stop(s)`}</div>
                </div>
                <div class="airport">
                    <div class="code">${flight.destination?.code || 'N/A'}</div>
                    <div class="name">${flight.destination?.name || 'Airport'}</div>
                    <div class="time">${formatTime(flight.destination?.time)}</div>
                    <div class="date">${formatDate(flight.destination?.time)}</div>
                </div>
            </div>
            <div class="flight-info" style="margin-top: 20px;">
                <div class="flight-info-item">
                    <div class="label">Airline</div>
                    <div class="value">${flight.airline}</div>
                </div>
                <div class="flight-info-item">
                    <div class="label">Flight Number</div>
                    <div class="value">${flight.flightNumber || 'N/A'}</div>
                </div>
                <div class="flight-info-item">
                    <div class="label">Class</div>
                    <div class="value">${fareType?.name || 'Economy'}</div>
                </div>
                <div class="flight-info-item">
                    <div class="label">Aircraft</div>
                    <div class="value">${flight.aircraft || 'Boeing 737'}</div>
                </div>
            </div>
        </div>
        
        <div class="section">
            <h3 class="section-title">Price Breakdown</h3>
            <div class="price-table">
                <div class="price-row">
                    <span>Fare (${totalTravelers} traveler${totalTravelers > 1 ? 's' : ''})</span>
                    <span>${formatPrice(fareType?.price || flight.price, flight.currency)} each</span>
                </div>
                <div class="price-row">
                    <span>Base Fare</span>
                    <span>${formatPrice(baseFare, flight.currency)}</span>
                </div>
                <div class="price-row">
                    <span>Taxes & Fees</span>
                    <span>${formatPrice(45, flight.currency)}</span>
                </div>
                <div class="price-row">
                    <span>Service Fee</span>
                    <span>${formatPrice(15, flight.currency)}</span>
                </div>
                <div class="price-row total">
                    <span>Total</span>
                    <span>${formatPrice(total, flight.currency)}</span>
                </div>
            </div>
        </div>
        
        <div class="section">
            <h3 class="section-title">Amenities</h3>
            <div class="amenities-list">
                <div class="amenity-item">
                    <div class="icon">✓</div>
                    <span>WiFi: ${flight.wifi ? 'Included' : 'Available'}</span>
                </div>
                <div class="amenity-item">
                    <div class="icon">✓</div>
                    <span>Meals: ${flight.meal ? 'Included' : 'Available'}</span>
                </div>
                <div class="amenity-item">
                    <div class="icon">✓</div>
                    <span>Baggage: ${flight.baggage || '7kg carry-on'}</span>
                </div>
                <div class="amenity-item">
                    <div class="icon">✓</div>
                    <span>Rating: ${flight.rating || 'N/A'}/5 stars</span>
                </div>
            </div>
        </div>
        
        <div class="section">
            <h3 class="section-title">Important Information</h3>
            <div class="info-box">
                <h4>📋 Check-in Requirements</h4>
                <p>Check-in opens 24 hours before departure. Closes 60 minutes before departure for domestic flights, 90 minutes for international.</p>
            </div>
            <div class="info-box">
                <h4>🔄 Cancellation Policy</h4>
                <p>Free cancellation up to 24 hours before departure. After that, a fee of ${formatPrice(50, flight.currency)} applies.</p>
            </div>
            <div class="info-box">
                <h4>💼 Baggage Allowance</h4>
                <p>${flight.baggage || '7kg carry-on'} included. Additional checked baggage available for purchase.</p>
            </div>
        </div>
        
        <div class="footer">
            <div class="logo">Frond App</div>
            <p>Generated on ${new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
        </div>
    </div>
</body>
</html>
    `.trim();
    
    return htmlContent;
  };

  // Download itinerary as PDF file
  const downloadItinerary = () => {
    try {
      const htmlContent = generateItineraryHTML();
      const routeCode = `${flight.departure?.code || 'DEP'}-${flight.destination?.code || 'ARR'}`;
      
      // Create a new window with the itinerary content
      const printWindow = window.open('', '_blank');
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      
      // Wait for content to load, then trigger print dialog (save as PDF)
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
      };
    } catch (err) {
      console.error('Error downloading itinerary:', err);
      alert('Failed to download itinerary. Please try again.');
    }
  };

  // Fare types
  const fareTypes = [
    { id: 'economy', name: 'Economy', price: flight?.price || 0, features: ['Standard seat', '7kg carry-on', 'Basic meal'] },
    { id: 'premium', name: 'Premium Economy', price: (flight?.price || 0) * 1.5, features: ['Extra legroom', '14kg carry-on', 'Premium meal', 'Priority check-in'] },
    { id: 'business', name: 'Business Class', price: (flight?.price || 0) * 3, features: ['Lie-flat seat', '32kg luggage', 'Gourmet meals', 'Airport lounge', 'Priority boarding'] },
    { id: 'first', name: 'First Class', price: (flight?.price || 0) * 5, features: ['Suite seat', '50kg luggage', 'Fine dining', 'Chauffeur service', 'Private suite'] }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin"></div>
            <Plane className="absolute inset-0 m-auto w-8 h-8 text-blue-500 animate-pulse" />
          </div>
          <p className="mt-4 text-lg text-gray-600">Loading flight details...</p>
        </div>
      </div>
    );
  }

  if (error || !flight) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Flight Not Found</h3>
          <p className="text-gray-600 mb-6">{error || 'The flight details could not be loaded'}</p>
          <div className="space-y-3">
            <button
              onClick={() => navigate(-1)}
              className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors"
            >
              Back to Results
            </button>
            <Link
              to={`/discover/${frondId}`}
              className="w-full border-2 border-gray-300 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-50 transition-colors block"
            >
              Search New Flight
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-900 to-blue-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-white/10 rounded-full transition-colors"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div>
                <h1 className="text-2xl font-bold">Flight Details</h1>
                <p className="text-blue-200">
                  {flight.departure?.code} to {flight.destination?.code} • {formatDate(flight.departure?.time)}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <button className="p-2 hover:bg-white/10 rounded-full">
                <Heart className="w-5 h-5" />
              </button>
              <button className="p-2 hover:bg-white/10 rounded-full">
                <Share2 className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="lg:grid lg:grid-cols-3 lg:gap-8">
          {/* Left Column - Flight Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Flight Overview Card */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl flex items-center justify-center">
                      <Plane className="w-7 h-7 text-blue-600" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">{flight.airline}</h2>
                      <p className="text-gray-600">Flight {flight.flightNumber} • {flight.aircraft || ''}</p>
                    </div>
                  </div>
                  {flight.rating && (
                    <div className="flex items-center gap-2 bg-gradient-to-r from-yellow-50 to-amber-50 px-4 py-2 rounded-lg">
                      <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                      <span className="font-bold text-gray-900">{flight.rating}/5</span>
                      <span className="text-gray-500">({flight.reviewCount || 120} reviews)</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Flight Timeline */}
              <div className="p-6">
                <div className="relative">
                  {/* Timeline */}
                  <div className="flex items-center justify-between">
                    {/* Departure */}
                    <div className="text-center">
                      <div className="text-3xl font-bold text-gray-900">{formatTime(flight.departure?.time)}</div>
                      <div className="text-sm text-gray-500 mt-2">{flight.departure?.code}</div>
                      <div className="text-xs text-gray-400 mt-1">{flight.departure?.name}</div>
                      <div className="mt-3">
                        <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium">
                          <Calendar className="w-4 h-4" />
                          {formatDate(flight.departure?.time)}
                        </div>
                      </div>
                    </div>

                    {/* Middle Section */}
                    <div className="flex-1 px-8">
                      <div className="text-center mb-4">
                        <div className="inline-flex items-center gap-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-1 rounded-full font-semibold">
                          <Clock className="w-5 h-5" />
                          {formatDuration(flight.durationMinutes)}
                        </div>
                      </div>
                      
                      <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                          <div className="w-full border-t-2 border-dashed border-gray-300"></div>
                        </div>
                        <div className="relative flex justify-between">
                          <div className="w-4 h-4 rounded-full bg-blue-600"></div>
                          <div className="w-4 h-4 rounded-full bg-blue-600"></div>
                        </div>
                      </div>
                      
                      <div className="text-center mt-4">
                        <div className="text-sm text-gray-600">
                          {flight.stops === 0 
                            ? 'Non-stop flight' 
                            : `${flight.stops} stop${flight.stops > 1 ? 's' : ''} via ${flight.layovers?.join(', ') || 'connecting airports'}`
                          }
                        </div>
                      </div>
                    </div>

                    {/* Arrival */}
                    <div className="text-center">
                      <div className="text-3xl font-bold text-gray-900">{formatTime(flight.destination?.time)}</div>
                      <div className="text-sm text-gray-500 mt-2">{flight.destination?.code}</div>
                      <div className="text-xs text-gray-400 mt-1">{flight.destination?.name}</div>
                      <div className="mt-3">
                        <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-medium">
                          <Calendar className="w-4 h-4" />
                          {formatDate(flight.destination?.time)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Flight Amenities */}
              <div className="p-2 bg-gradient-to-r from-gray-50 to-blue-50 border-t border-gray-100">
                <h3 className="font-bold text-lg text-gray-900 mb-4 ml-4">Onboard Amenities</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <div className="flex items-center gap-3 p-3  bg-white rounded-xl shadow-sm">
                    <div className="w-10 h-10 bg-blue-100  rounded-lg flex items-center justify-center">
                      <Wifi className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">WiFi</p>
                      <p className="text-xs text-gray-500">{flight.wifi ? 'Free' : 'Available'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-white rounded-xl shadow-sm">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <Coffee className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Meals</p>
                      <p className="text-xs text-gray-500">{flight.meal ? 'Included' : 'Available'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-white rounded-xl shadow-sm">
                    <div className="w-12 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Luggage className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Baggage</p>
                      <p className="text-xs text-gray-500">{flight.baggage || '7kg carry-on'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-white rounded-xl shadow-sm">
                    <div className="w-12 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                      <Shield className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Insurance</p>
                      <p className="text-xs text-gray-500">Covered</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Fare Options */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
              <div className="p-6 border-b border-gray-100">
                <h3 className="text-xl font-bold text-gray-900">Select Fare Type</h3>
                <p className="text-gray-600 mt-1">Choose the class that suits your travel needs</p>
              </div>
              
              <div className="p-6">
                <div className="space-y-4">
                  {fareTypes.map((fare) => (
                    <div
                      key={fare.id}
                      onClick={() => setSelectedFare(fare.id)}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        selectedFare === fare.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-bold text-lg text-gray-900">{fare.name}</h4>
                          <div className="mt-2 space-y-1">
                            {fare.features.map((feature, index) => (
                              <div key={index} className="flex items-center gap-2 text-sm text-gray-600">
                                <CheckCircle className="w-4 h-4 text-green-500" />
                                {feature}
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-blue-600">
                            {formatPrice(fare.price, flight.currency)}
                          </div>
                          <p className="text-sm text-gray-500">per person</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Flight Policies */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
              <div className="p-6 border-b border-gray-100">
                <h3 className="text-xl font-bold text-gray-900">Flight Policies</h3>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Shield className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Cancellation Policy</h4>
                      <p className="text-gray-600 text-sm mt-1">
                        Free cancellation up to 24 hours before departure. After that, a fee of {formatPrice(50, flight.currency)} applies.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Check-in Requirements</h4>
                      <p className="text-gray-600 text-sm mt-1">
                        Check-in opens 24 hours before departure and closes 60 minutes before departure for domestic flights, 90 minutes for international.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Luggage className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Baggage Allowance</h4>
                      <p className="text-gray-600 text-sm mt-1">
                        {flight.baggage || '7kg carry-on'} included. Additional checked baggage available for purchase.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Booking Summary */}
          <div className="lg:col-span-1">
            <div className="sticky top-8">
              {/* Booking Card */}
              <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white">
                  <h3 className="text-xl font-bold">Booking Summary</h3>
                  <p className="text-blue-100 mt-1">Review and book your flight</p>
                </div>
                
                <div className="p-6">
                  {/* Selected Fare */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold text-gray-900">Selected Fare</h4>
                        <p className="text-sm text-gray-500">
                          {fareTypes.find(f => f.id === selectedFare)?.name || 'Economy'}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-blue-600">
                          {formatPrice(
                            fareTypes.find(f => f.id === selectedFare)?.price || flight.price,
                            flight.currency
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Traveler Count */}
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3">Travelers</h4>
                    <div className="space-y-3">
                      {[
                        { label: 'Adults (12+)', count: passengerDetails.adults },
                        { label: 'Children (2-11)', count: passengerDetails.children },
                        { label: 'Infants (<2)', count: passengerDetails.infants }
                      ].map((item, index) => (
                        <div key={index} className="flex items-center justify-between">
                          <span className="text-gray-600">{item.label}</span>
                          <span className="font-semibold text-gray-900">{item.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Price Breakdown */}
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3">Price Breakdown</h4>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Base Fare</span>
                        <span>{formatPrice(
                          (fareTypes.find(f => f.id === selectedFare)?.price || flight.price) * 
                          (passengerDetails.adults + passengerDetails.children + passengerDetails.infants),
                          flight.currency
                        )}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Taxes & Fees</span>
                        <span>{formatPrice(45, flight.currency)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Service Fee</span>
                        <span>{formatPrice(15, flight.currency)}</span>
                      </div>
                      <div className="border-t border-gray-200 pt-2 mt-2">
                        <div className="flex items-center justify-between">
                          <span className="font-bold text-gray-900">Total</span>
                          <span className="text-2xl font-bold text-blue-600">
                            {formatPrice(
                              ((fareTypes.find(f => f.id === selectedFare)?.price || flight.price) * 
                              (passengerDetails.adults + passengerDetails.children + passengerDetails.infants)) + 60,
                              flight.currency
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-3">
                    <button
                      onClick={() => handleBookOnExpedia(flight, flight.currency)}
                      className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-4 rounded-xl font-bold text-lg hover:opacity-90 transition-all flex items-center justify-center gap-2"
                    >
                      <ExternalLink className="w-5 h-5" />
                      Book on Expedia
                    </button>
                    
                    <button
                      onClick={() => setShowBookingForm(true)}
                      className="w-full border-2 border-blue-600 text-blue-600 py-3 rounded-xl font-semibold hover:bg-blue-50 transition-colors"
                    >
                      Save for Later
                    </button>
                  </div>

                  {/* Security Badge */}
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                      <Lock className="w-4 h-4" />
                      <span>Secure SSL Encryption</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="mt-4 bg-white rounded-2xl shadow-lg border border-gray-200 p-4">
                <h4 className="font-semibold text-gray-900 mb-3">Quick Actions</h4>
                <div className="space-y-2">
                  <button
                    onClick={downloadItinerary}
                    className="w-full flex items-center justify-between p-3 hover:bg-gray-50 rounded-xl transition-colors"
                  >
                    <span className="text-gray-700">Download Itinerary (PDF)</span>
                    <Download className="w-4 h-4 text-gray-400" />
                  </button>
                  <button
                    onClick={() => handleHotelPackageOnBooking(flight, searchParams)}
                    className="w-full flex items-center justify-between p-3 hover:bg-gray-50 rounded-xl transition-colors text-blue-600"
                  >
                    <span className="font-medium">🏨 Search Hotel</span>
                    <ExternalLink className="w-4 h-4" />
                  </button>
                  {/* <button className="w-full flex items-center justify-between p-3 hover:bg-gray-50 rounded-xl transition-colors">
                    <span className="text-gray-700">Add Travel Insurance</span>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </button> */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlightDetails;